import { useForm } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  PieChart,
  TrendingUp,
  User,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import z from 'zod';
import { authClient } from '@/lib/auth-client';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

interface ModernAuthFormProps {
  initialMode?: 'signin' | 'signup';
  prefilledEmail?: string;
  redirectTo?: string;
}

export default function ModernAuthForm({
  initialMode = 'signin',
  prefilledEmail,
  redirectTo,
}: ModernAuthFormProps) {
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate({ from: '/' });
  const { isPending } = authClient.useSession();

  const signInForm = useForm({
    defaultValues: {
      email: prefilledEmail || '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      await authClient.signIn.email(
        {
          email: value.email,
          password: value.password,
        },
        {
          onSuccess: () => {
            navigate({ to: redirectTo || '/dashboard' });
            toast.success('Bem-vindo de volta!');
          },
          onError: (error) => {
            toast.error(error.error.message || 'Erro ao fazer login');
          },
        }
      );
    },
    validators: {
      onSubmit: z.object({
        email: z.string().email('Email inválido'),
        password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
      }),
    },
  });

  const signUpForm = useForm({
    defaultValues: {
      name: '',
      email: prefilledEmail || '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      await authClient.signUp.email(
        {
          email: value.email,
          password: value.password,
          name: value.name,
        },
        {
          onSuccess: () => {
            navigate({ to: redirectTo || '/dashboard' });
            toast.success('Conta criada com sucesso!');
          },
          onError: (error) => {
            toast.error(error.error.message || 'Erro ao criar conta');
          },
        }
      );
    },
    validators: {
      onSubmit: z.object({
        name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
        email: z.string().email('Email inválido'),
        password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
      }),
    },
  });

  const isSignIn = mode === 'signin';
  const currentForm = isSignIn ? signInForm : signUpForm;

  return (
    <div className="flex min-h-screen">
      {/* Left side - Form */}
      <div className="flex flex-1 items-center justify-center bg-background p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="mb-4 flex items-center justify-center">
              <Wallet className="mr-2 h-8 w-8 text-foreground" />
              <span className="font-bold text-2xl text-foreground">
                Control Money
              </span>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="text-center">
            <h2 className="mb-2 font-bold text-3xl text-foreground">
              {isSignIn ? 'Bem-vindo de volta' : 'Crie sua conta'}
            </h2>
            <p className="text-muted-foreground">
              {isSignIn
                ? 'Entre em sua conta para continuar'
                : 'Junte-se a milhões de pessoas que confiam em nós para gerenciar suas finanças'}
            </p>
          </div>

          {/* Auth Tabs */}
          <div className="flex rounded-lg bg-muted p-1">
            <button
              className={`flex-1 rounded-md px-4 py-2 font-medium text-sm transition-all ${
                isSignIn
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setMode('signin')}
              type="button"
            >
              Entrar
            </button>
            <button
              className={`flex-1 rounded-md px-4 py-2 font-medium text-sm transition-all ${
                isSignIn
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'bg-background text-foreground shadow-sm'
              }`}
              onClick={() => setMode('signup')}
              type="button"
            >
              Registrar
            </button>
          </div>

          {/* Form */}
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              currentForm.handleSubmit();
            }}
          >
            {/* Name field - only for signup */}
            {!isSignIn && (
              <signUpForm.Field name="name">
                {(field) => (
                  <div>
                    <Label
                      className="mb-2 block font-medium text-foreground text-sm"
                      htmlFor={field.name}
                    >
                      Nome completo
                    </Label>
                    <div className="relative">
                      <User className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 transform text-muted-foreground" />
                      <Input
                        className="h-12 pl-10"
                        id={field.name}
                        name={field.name}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        placeholder="Seu nome completo"
                        type="text"
                        value={field.state.value}
                      />
                    </div>
                    {field.state.meta.errors.map((error) => (
                      <p
                        className="mt-1 text-red-600 text-sm"
                        key={error?.message}
                      >
                        {error?.message}
                      </p>
                    ))}
                  </div>
                )}
              </signUpForm.Field>
            )}

            {/* Email field */}
            <currentForm.Field name="email">
              {(field) => (
                <div>
                  <Label
                    className="mb-2 block font-medium text-foreground text-sm"
                    htmlFor={field.name}
                  >
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 transform text-muted-foreground" />
                    <Input
                      className={`h-12 pl-10 ${
                        prefilledEmail ? 'bg-muted text-muted-foreground' : ''
                      }`}
                      disabled={!!prefilledEmail}
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="seu@email.com"
                      type="email"
                      value={field.state.value}
                    />
                  </div>
                  {field.state.meta.errors.map((error) => (
                    <p
                      className="mt-1 text-red-600 text-sm"
                      key={error?.message}
                    >
                      {error?.message}
                    </p>
                  ))}
                </div>
              )}
            </currentForm.Field>

            {/* Password field */}
            <currentForm.Field name="password">
              {(field) => (
                <div>
                  <Label
                    className="mb-2 block font-medium text-foreground text-sm"
                    htmlFor={field.name}
                  >
                    Senha
                  </Label>
                  <div className="relative">
                    <Lock className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 transform text-muted-foreground" />
                    <Input
                      className="h-12 pr-10 pl-10"
                      id={field.name}
                      name={field.name}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Sua senha"
                      type={showPassword ? 'text' : 'password'}
                      value={field.state.value}
                    />
                    <button
                      className="-translate-y-1/2 absolute top-1/2 right-3 transform text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                      type="button"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {field.state.meta.errors.map((error) => (
                    <p
                      className="mt-1 text-red-600 text-sm"
                      key={error?.message}
                    >
                      {error?.message}
                    </p>
                  ))}
                </div>
              )}
            </currentForm.Field>

            {/* Submit button */}
            <currentForm.Subscribe>
              {(state) => (
                <Button
                  className="h-12 w-full rounded-lg font-medium transition-colors"
                  disabled={!state.canSubmit || state.isSubmitting || isPending}
                  type="submit"
                >
                  {state.isSubmitting || isPending
                    ? 'Carregando...'
                    : isSignIn
                      ? 'Entrar'
                      : 'Criar conta'}
                </Button>
              )}
            </currentForm.Subscribe>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-border border-t" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-background px-2 text-muted-foreground">
                Ou continue com
              </span>
            </div>
          </div>

          {/* Social Login */}
          <div className="grid grid-cols-3 gap-3">
            <Button className="h-12" type="button" variant="outline">
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="currentColor"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="currentColor"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="currentColor"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="currentColor"
                />
              </svg>
            </Button>
            <Button className="h-12" type="button" variant="outline">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.024-.105-.949-.199-2.403.041-3.439.219-.937 1.404-5.965 1.404-5.965s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.751-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001.012.001z" />
              </svg>
            </Button>
            <Button className="h-12" type="button" variant="outline">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </Button>
          </div>

          {/* Bottom text */}
          <p className="text-center text-muted-foreground text-sm">
            {isSignIn ? (
              <>
                Não tem uma conta?{' '}
                <button
                  className="font-medium text-foreground hover:underline"
                  onClick={() => setMode('signup')}
                  type="button"
                >
                  Registre-se
                </button>
              </>
            ) : (
              <>
                Já tem uma conta?{' '}
                <button
                  className="font-medium text-foreground hover:underline"
                  onClick={() => setMode('signin')}
                  type="button"
                >
                  Entre aqui
                </button>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="relative hidden flex-1 bg-gradient-to-br from-black via-gray-900 to-gray-800 lg:flex">
        <div className="absolute inset-0 bg-white/5" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white">
          <div className="max-w-md space-y-6 text-center">
            <h3 className="font-bold text-4xl">
              Controle suas finanças de forma inteligente
            </h3>
            <p className="text-gray-300 text-lg">
              Faça login para acessar seu painel personalizado, acompanhar seu
              portfólio e tomar decisões financeiras informadas.
            </p>

            {/* Features */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center space-x-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <span className="text-gray-300">
                  Análise de tendências em tempo real
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                  <PieChart className="h-5 w-5" />
                </div>
                <span className="text-gray-300">
                  Relatórios detalhados e insights
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                  <Wallet className="h-5 w-5" />
                </div>
                <span className="text-gray-300">
                  Gestão inteligente de orçamentos
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Background decorative elements */}
        <div className="absolute top-20 left-20 h-32 w-32 rounded-full bg-white/10 blur-xl" />
        <div className="absolute right-20 bottom-20 h-40 w-40 rounded-full bg-white/10 blur-xl" />
        <div className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 h-96 w-96 transform rounded-full bg-white/5 blur-3xl" />
      </div>
    </div>
  );
}
